import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Link as MuiLink,
  Button,
  Paper,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WarningIcon from "@mui/icons-material/Warning";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getImageUrl } from "../../../utils/helpers";

const AiEvaluation = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <AssessmentIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          AI Application Evaluation
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
        }}
      >
        {/* Overview Section with Image Left, Text Right */}
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImageUrl("kth-ai-guidance.png")}
              alt="AI Application Evaluation"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color={tertColor}
              gutterBottom
            >
              Overview of Our AI Evaluation Services
            </Typography>
            <Typography variant="body1" paragraph>
              Kama Thuo, PLLC provides comprehensive AI counseling services,
              including guidance on the legal and ethical sourcing of labeling
              services, and legal training on AI technologies and best
              practices. We also support clients in testing and evaluating AI
              outputs for bias and legal compliance.
            </Typography>
            <Typography variant="body1" paragraph>
              Our services extend to ensuring AI systems adhere to privacy and
              data protection laws, offering strategic advice to navigate the
              complex regulatory landscape surrounding artificial intelligence.
              We help businesses and organizations understand the legal
              implications of their AI implementations and develop strategies to
              mitigate risks.
            </Typography>

            <Box sx={{ mt: 2 }}>
              {[
                "AI Bias",
                "Regulatory Compliance",
                "Rule 11 Investigations",
                "AI Output Analysis",
                "Intellectual Property Risk",
              ].map((label, i) => (
                <Chip label={label} key={i} sx={{ m: 0.5 }} />
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Full Width Alert Box */}
        <Box sx={{ mt: 4, mb: 6 }}>
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{
              borderRadius: 2,
              p: 3,
              backgroundColor: "#fff8e1",
              border: `1px solid ${tertColor}44`,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Free & Open Source (FOSS) Licensing Risks
            </Typography>
            <Typography variant="body2" paragraph>
              Many AI applications incorporate open source libraries that may
              have complex licensing requirements. We help you identify and
              navigate these license compliance issues to avoid potential legal
              challenges.
            </Typography>

            <MuiLink
              component={RouterLink}
              to="/software-licensing#foss-risks"
              color="primary"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: "none",
                // mt: 1,

                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" component="span">
                Learn more about FOSS risks
              </Typography>
              <ChevronRightIcon sx={{ ml: 0.5 }} />
            </MuiLink>
          </Alert>
        </Box>

        {/* Key Evaluation Areas */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            Key Evaluation Areas
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                icon: <SecurityIcon />,
                title: "Legal & Ethical Compliance",
                text: "Assessment of AI systems against relevant laws, regulations, and ethical frameworks",
              },
              {
                icon: <GavelIcon />,
                title: "Contract & License Analysis",
                text: "Review of third-party AI provider agreements and licensing terms",
              },
              {
                icon: <AssessmentIcon />,
                title: "Output & Performance Evaluation",
                text: "Testing AI-generated results for accuracy, bias, and potential IP infringement",
              },
              {
                icon: <SecurityIcon />,
                title: "Privacy & Data Protection",
                text: "Assessment of AI data handling practices and compliance with privacy regulations",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: `1px solid ${tertColor}22`,
                    borderRadius: 2,
                    backgroundColor: "#f9f9f9",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: `${tertColor}08`,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box sx={{ color: tertColor, mr: 1 }}>{item.icon}</Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Legal Agreements Section */}
        <Divider sx={{ my: 4 }} />
        <Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            Legal Agreements for AI Applications
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph>
                We deliver comprehensive services to draft, analyze, and
                optimize AI-related legal agreements, including SaaS contracts,
                model and data licensing agreements, IP licensing deals,
                technology transfer agreements, joint development contracts, and
                confidentiality and non-disclosure agreements.
              </Typography>
              <Typography variant="body1" paragraph>
                We possess deep insights into critical legal terms and
                conditions essential in AI contracts, enabling our clients to
                proactively manage and minimize contractual risks. You can also
                explore specific legal clauses and provisions relevant to your
                AI transactions using our proprietary{" "}
                <MuiLink
                  component={RouterLink}
                  to="/legal-terms-checker-for-ai"
                >
                  legal terms checker tool.
                </MuiLink>
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Button
                  startIcon={<LaunchIcon />}
                  variant="contained"
                  sx={{
                    mr: 2,
                    mb: { xs: 2, md: 0 },
                    backgroundColor: tertColor,
                    color: "#fff",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                  component={RouterLink}
                  to="/legal-agreements-for-ai"
                >
                  AI Tool Agreements
                </Button>
                <Button
                  startIcon={<LaunchIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: tertColor,
                    color: "#fff",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                  component={RouterLink}
                  to="/legal-terms-for-ai"
                >
                  AI Tool Legal Terms
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${tertColor} 0%, ${tertColor}CC 100%)`,
                    p: 2.5,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="white"
                    align="center"
                  >
                    AI Agreement Types We Specialize In
                  </Typography>
                </Box>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, backgroundColor: "white" }}>
                    <Grid container spacing={1.5}>
                      {[
                        "AI SaaS Agreements",
                        "Model Licensing",
                        "Data Usage & Training",
                        "API Integration",
                        "Development Agreements",
                        "AI Consulting",
                      ].map((agreement, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e0e0e0",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: `${tertColor}11`,
                                borderColor: `${tertColor}66`,
                                transform: "translateX(5px)",
                              },
                            }}
                          >
                            <GavelIcon
                              sx={{
                                color: tertColor,
                                mr: 1.5,
                                fontSize: "small",
                              }}
                            />
                            <Typography variant="body2" fontWeight="medium">
                              {agreement}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Regulatory & Industry Compliance Section */}
        <Divider sx={{ my: 4 }} />
        <Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            Regulatory & Industry Compliance
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" paragraph>
                Kama Thuo, PLLC assists clients in creating robust AI governance
                frameworks to ensure regulatory compliance and adherence to
                industry best practices. Our AI regulatory compliance services
                cover relevant global regulations (GDPR, CCPA, FTC guidelines)
                and industry standards specific to AI and data governance.
              </Typography>
              <Typography variant="body1" paragraph>
                We offer tailored compliance assessments, strategic policy
                formulation, and practical recommendations for operationalizing
                AI governance frameworks. Identify and address compliance risks
                specific to your AI applications using our intuitive and
                detailed AI compliance checker tool.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Button
                  startIcon={<LaunchIcon />}
                  variant="contained"
                  sx={{
                    mr: 2,
                    mb: 2,
                    backgroundColor: tertColor,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: tertHoverColor,
                    },
                  }}
                  component={RouterLink}
                  to="/compliance-checker-for-ai"
                >
                  Try Compliance Checker
                </Button>
                <Button
                  startIcon={<LaunchIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: priColor,
                    mb: 2,
                    color: "white",
                    "&:hover": {
                      color: "white",
                      backgroundColor: priHoverColor,
                    },
                  }}
                  component={RouterLink}
                  to="/news-insights?category=ai-counseling"
                >
                  News & Insights
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={tertColor}
                  gutterBottom
                >
                  Key Compliance Areas
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {[
                    "Data privacy and protection regulations",
                    "AI transparency and explainability requirements",
                    "Fairness and non-discrimination principles",
                    "Industry-specific AI regulations",
                    "AI risk assessment frameworks",
                  ].map((area, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "flex-start" }}
                    >
                      <ChevronRightIcon
                        sx={{ color: tertColor, mt: 0.25, mr: 1 }}
                        fontSize="small"
                      />
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {area}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiEvaluation;
